import React, { useRef, useState } from 'react';
import { Grid, OutlinedInput, InputLabel, Stack, FormHelperText, Box, Typography, CircularProgress } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTheme } from '@mui/material/styles';
import testLogoImg from 'assets/images/icons/test_logo.png';
import testThumbImg from 'assets/images/icons/test_thum.png';
import { SUPPORT_IMAGE_TYPE, maxFileSizeAllow } from 'constant/AppConfig';
import { toast } from 'react-toastify';
import { apiRequest } from 'helpers/apiRequests';
import ImageCropper from 'components/@extended/ImageCropper';

const Theme = ({
  control,
  errors,
  primary,
  setPrimary,
  secondary,
  setSecondary,
  logo,
  setLogo,
  thumbnail,
  setThumbnail,
  setValue,
  setError
}) => {
  const theme = useTheme();
  const logoInputRef = useRef(null);
  const inputOneRef = useRef(null);
  const inputTwoRef = useRef(null);
  const thumbInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenModal, setOpenModal] = useState(false);
  const [file, setFile] = useState();

  const boxStyle = {
    height: '60px',
    border: `1px dashed ${theme.palette.primary.main}`,
    background: theme.palette.primary.lighter,
    color: theme.palette.primary.main,
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    padding: '0px 21px',
    cursor: 'pointer'
  };

  const inputStyle = {
    width: '35px',
    height: '35px',
    padding: '0px',
    // borderRadius: '50% !important',
    overflow: 'hidden',
    position: 'relative',
    input: {
      padding: '0px !important',
      width: '35px',
      height: '35px',
      // borderRadius: '50% !important',
      zIndex: 1,
      outline: 'none'
    },
    fieldset: {
      border: 'none !important',
      outline: 'none'
    }
  };

  const handleFileChange = async (e, type) => {
    try {
      const file = e.target.files;
      if (file) {
        if (!SUPPORT_IMAGE_TYPE.includes(file[0]?.type)) {
          toast.error('Uploaded file is not image!');
          return;
        }
        if (file[0].size >= maxFileSizeAllow * 1024 * 1024) {
          toast.error(`Uploaded image is more than ${maxFileSizeAllow} MB.`);
          return;
        }
        setFile(file[0]);
        setOpenModal(type);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
      if (type === 'logo') {
        logoInputRef.current.value = null;
      } else {
        thumbInputRef.current.value = null;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (type) => {
    if (type === 'logo') {
      logoInputRef.current.click();
    } else {
      thumbInputRef.current.click();
    }
  };

  const handleClose = async (file) => {
    const imageType = isOpenModal;
    setOpenModal(false);
    try {
      if (file === '') throw Error('');
      if (imageType === 'logo') {
        setIsLoading('logo');
        const response = await apiRequest.uploadProfileImage(file, 'tenant-logo', 'logo');
        if (response?.data.status === 200) {
          toast.success(response?.data?.message);
          setValue('company_logo', response?.data?.data?.image_name);
          setError('company_logo', '');
          setLogo(response?.data?.data?.image_url);
        }
      } else {
        setIsLoading('thumbnail');
        const response = await apiRequest.uploadProfileImage(file, 'tenant-logo', 'thumbnail');
        if (response?.data?.status === 200) {
          toast.success(response?.data?.message);
          setError('company_thumbnail', '');
          setValue('company_thumbnail', response?.data?.data?.image_name);
          setThumbnail(response?.data?.data?.image_url);
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
      if (error?.response?.status === 413) {
        toast.error('Uploaded image is more than 5 MB.');
      }
      if (imageType === 'logo') {
        logoInputRef.current.value = null;
      } else {
        thumbInputRef.current.value = null;
      }
    } finally {
      setFile(null);
      setOpenModal(false);
      setIsLoading(false);
    }
  };

  // React.useEffect(() => {
  //   if (themeInfo) {
  //     setValue('company_logo', themeInfo?.logo?.image_name);
  //     setValue('company_thumbnail', themeInfo?.thumbnail?.image_name);
  //     setValue('primary_color', themeInfo?.primary_color);
  //     setValue('secondary_color', themeInfo?.secondary_color);
  //     setLogo(themeInfo?.logo);
  //     setThumbnail(themeInfo?.thumbnail);
  //     setPrimary(themeInfo?.primary_color);
  //     setSecondary(themeInfo?.secondary_color);
  //   }
  // }, [themeInfo]);

  return (
    <Box>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
        >
          <Stack spacing={1}>
            <InputLabel htmlFor="mission">Company Logo</InputLabel>
            <Box
              sx={boxStyle}
              onClick={() => handleFileUpload('logo')}
            >
              <Stack
                direction="row"
                spacing={1}
                width={'100%'}
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                >
                  <Typography
                    fontWeight={600}
                    variant="subtitle1"
                    sx={{ fontSize: { xs: '10px', sm: '15px' } }}
                  >
                    Upload logo (128*44 px)
                  </Typography>
                  <Typography
                    fontWeight={300}
                    fontSize={12}
                    lineHeight={'21px'}
                    variant="subtitle2"
                  >
                    {logo ? '' : 'Upload rectangular logo for company. Example:'}
                  </Typography>
                </Stack>
                {isLoading === 'logo' ? (
                  <Box sx={{ width: 'auto', height: 'auto', textAlign: 'center' }}>
                    <CircularProgress
                      sx={{ width: '25px !important', height: '25px !important' }}
                      color="primary"
                    />
                  </Box>
                ) : (
                  <img
                    src={logo ?? testLogoImg}
                    alt="logo"
                    style={{ width: 'auto', height: '44px' }}
                  />
                )}
                <input
                  style={{ display: 'none' }}
                  type="file"
                  ref={logoInputRef}
                  id="fileupload"
                  onChange={(e) => handleFileChange(e, 'logo')}
                />
              </Stack>
            </Box>
            {errors.company_logo?.message && (
              <FormHelperText
                error
                id="standard-weight-helper-text-company_logo"
              >
                {errors.company_logo?.message}
              </FormHelperText>
            )}
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
        >
          <Stack spacing={1}>
            <InputLabel htmlFor="mission">Company Thumbnail</InputLabel>
            <Box
              sx={boxStyle}
              onClick={() => handleFileUpload('thumb')}
            >
              <Stack
                direction="row"
                width={'100%'}
                spacing={1}
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <Stack
                  direction={'row'}
                  spacing={0.5}
                >
                  <Typography
                    fontWeight={600}
                    variant="subtitle1"
                    sx={{ fontSize: { xs: '10px', sm: '15px' } }}
                  >
                    Upload thumbnail (64*64 px)
                  </Typography>
                  <Typography
                    fontWeight={300}
                    fontSize={12}
                    lineHeight={'21px'}
                    variant="subtitle2"
                  >
                    {thumbnail ? '' : 'Upload square logo for company. Example:'}
                  </Typography>
                </Stack>
                {isLoading === 'thumbnail' ? (
                  <Box sx={{ width: '44px', height: 'auto' }}>
                    <CircularProgress
                      sx={{ width: '25px !important', height: '25px !important' }}
                      color="primary"
                    />
                  </Box>
                ) : (
                  <img
                    style={{ width: '44px', height: 'auto' }}
                    src={thumbnail ?? testThumbImg}
                    alt="logo"
                  />
                )}
              </Stack>
              <input
                style={{ display: 'none' }}
                type="file"
                ref={thumbInputRef}
                id="fileupload"
                onChange={(e) => handleFileChange(e, 'thumb')}
              />
            </Box>
            {errors.company_thumbnail?.message && (
              <FormHelperText
                error
                id="standard-weight-helper-text-company_thumbnail"
              >
                {errors.company_thumbnail?.message}
              </FormHelperText>
            )}
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
        >
          <Stack spacing={1}>
            <InputLabel htmlFor="mission">Primary Color</InputLabel>
            <Box
              sx={{
                borderRadius: '8px',
                border: '1px solid #CBD5E1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '5px 10px',
                cursor: 'pointer'
              }}
              onClick={() => inputOneRef.current?.click()}
            >
              <Typography
                variant="subtitle1"
                color="secondary.light"
              >
                {primary}{' '}
              </Typography>
              <Controller
                name="primary_color"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <OutlinedInput
                    ref={inputOneRef}
                    id="primary_color"
                    type="color"
                    value={field.value}
                    name={field.name}
                    onBlur={field.onBlur}
                    onChange={(e) => {
                      field.onChange(e);
                      setTimeout(() => {
                        setPrimary(e.target.value);
                      }, 1500);
                    }}
                    placeholder="Company Name"
                    fullWidth
                    sx={inputStyle}
                    error={Boolean(errors.primary_color)}
                  />
                )}
              />
            </Box>
            {errors.primary_color?.message && (
              <FormHelperText
                error
                id="standard-weight-helper-text-primary_color"
              >
                {errors.primary_color?.message}
              </FormHelperText>
            )}
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
        >
          <Stack spacing={1}>
            <InputLabel htmlFor="mission">Secondary Color</InputLabel>
            <Box
              sx={{
                borderRadius: '8px',
                border: '1px solid #CBD5E1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '5px 10px',
                cursor: 'pointer'
              }}
            >
              <Typography
                variant="subtitle1"
                color="secondary.light"
              >
                {secondary}
              </Typography>
              <Controller
                name="secondary_color"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <OutlinedInput
                    ref={inputTwoRef}
                    id="secondary_color"
                    type="color"
                    value={field.value}
                    name={field.name}
                    onBlur={field.onBlur}
                    onChange={(e) => {
                      field.onChange(e);
                      setTimeout(() => {
                        setSecondary(e.target.value);
                      }, 1500);
                    }}
                    placeholder="Company Name"
                    fullWidth
                    sx={inputStyle}
                    error={Boolean(errors.secondary_color)}
                  />
                )}
              />
            </Box>

            {errors.secondary_color?.message && (
              <FormHelperText
                error
                id="standard-weight-helper-text-secondary_color"
              >
                {errors.secondary_color?.message}
              </FormHelperText>
            )}
          </Stack>
        </Grid>
      </Grid>
      <ImageCropper
        image={file}
        isOpenModal={isOpenModal}
        handleClose={handleClose}
        cropRatio={isOpenModal === 'logo' ? 3 / 1 : 1 / 1}
      />
    </Box>
  );
};

export default Theme;
